(() => {
  const triggers = document.querySelectorAll('[data-mobile-menu-trigger]');

  if (!triggers.length || typeof HTMLDialogElement === 'undefined') return;

  const desktopViewport = window.matchMedia('(min-width: 768px)');

  triggers.forEach((trigger) => {
    const dialogId = trigger.getAttribute('aria-controls');

    if (!dialogId) return;

    const dialog = document.getElementById(dialogId);

    if (!(dialog instanceof HTMLDialogElement)) return;

    const closeButton = dialog.querySelector('[data-mobile-menu-close]');

    /*
     * Read from the element, not from a constant. This was a hardcoded 200 that
     * duplicated the 200ms in the dialog's own CSS - two sources for one number,
     * and the CSS one moves.
     *
     * It also broke under prefers-reduced-motion. assets/theme.css collapses every
     * transition-duration to .01ms there, so the menu vanished instantly while this
     * timer still held it open() and modal for a further 200ms: focus trapped inside
     * an invisible dialog, the page scroll-locked behind it, Escape and Tab both
     * landing somewhere the user cannot see. Short, but it is exactly the window a
     * reduced-motion user is in every single time they close the menu.
     *
     * getComputedStyle returns the used value, so it tracks the media query for free.
     */
    const closeDelay = () => {
      const declared = getComputedStyle(dialog).transitionDuration.split(',')[0].trim();
      const seconds = declared.endsWith('ms') ? parseFloat(declared) / 1000 : parseFloat(declared);

      return Number.isFinite(seconds) ? seconds * 1000 : 0;
    };

    let closeTimer;

    const finishClose = () => {
      window.clearTimeout(closeTimer);
      dialog.classList.remove('is-open', 'is-closing', 'is-search-first');

      if (dialog.open) dialog.close();
    };

    const closeMenu = (immediate = false) => {
      if (!dialog.open || dialog.classList.contains('is-closing')) return;

      if (immediate) {
        finishClose();
        return;
      }

      dialog.classList.add('is-closing');
      dialog.classList.remove('is-open');
      closeTimer = window.setTimeout(finishClose, closeDelay());
    };

    /*
     * The search icon is a second trigger for the same drawer. It is a link to the
     * search page, which is what it stays without this script; with it, the click
     * is taken over and the drawer opens on its search field. The focus call is
     * synchronous, inside the click, and not in the requestAnimationFrame below:
     * iOS raises the keyboard only for a focus that happens inside the gesture.
     */
    const searchField = trigger.hasAttribute('data-mobile-search-trigger')
      ? dialog.querySelector('[data-psearch-input]')
      : null;

    trigger.addEventListener('click', (event) => {
      if (dialog.open || typeof dialog.showModal !== 'function') return;

      if (searchField) {
        event.preventDefault();
        // The field sits under the nav list. Scrolling it into view scrolled the
        // drawer head, close button and all, off the top of the screen, so the
        // form is moved up the drawer's flex column instead (header.liquid).
        dialog.classList.add('is-search-first');
      }

      dialog.showModal();
      trigger.setAttribute('aria-expanded', 'true');

      if (searchField) searchField.focus();

      window.requestAnimationFrame(() => {
        dialog.classList.add('is-open');
      });
    });

    if (closeButton) {
      closeButton.addEventListener('click', () => closeMenu());
    }

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeMenu();
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeMenu();
    });

    dialog.addEventListener('close', () => {
      window.clearTimeout(closeTimer);
      dialog.classList.remove('is-open', 'is-closing', 'is-search-first');
      trigger.setAttribute('aria-expanded', 'false');
    });

    desktopViewport.addEventListener('change', (event) => {
      if (event.matches) closeMenu(true);
    });
  });
})();

/*
 * The full-size viewer.
 *
 * Every panel carries a link to its own full image, which is the whole feature with
 * no script. Here the link opens a modal dialog instead: the photograph contained,
 * never cropped. A loupe follows the pointer or sits clear of a dragging finger;
 * the slider sets its strength. The keyboard can move it too, without moving to
 * another photograph. Escape closes the dialog and focus returns to the link.
 */
(() => {
  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const viewer = gallery.parentElement.querySelector('[data-gallery-viewer]');
    const links = Array.from(gallery.querySelectorAll('[data-gallery-zoom]'));

    if (!viewer || !links.length || typeof viewer.showModal !== 'function') return;

    const image = viewer.querySelector('[data-viewer-image]');
    const stage = viewer.querySelector('[data-viewer-stage]');
    const count = viewer.querySelector('[data-viewer-count]');
    const loupe = viewer.querySelector('[data-viewer-loupe]');
    const level = viewer.querySelector('[data-viewer-level]');
    const levelOut = viewer.querySelector('[data-viewer-level-out]');
    const hint = viewer.querySelector('[data-viewer-hint]');
    const hoverHint = hint.textContent;
    const minimum = Number(level.min);
    const maximum = Number(level.max);
    const step = Number(level.step);
    let current = 0;
    let opener = null;
    let previousOverflow = '';
    let zoom = Number(level.value);
    let mode = null;
    let point = { x: 0, y: 0 };
    let touchPointer = null;
    let frame = null;
    let wheelDelta = 0;
    let mouseBackdrop = false;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    /*
     * Contain can leave space inside the image element. Measure the painted photo
     * from its natural ratio so neither that space nor a rotation shifts the loupe.
     */
    const photoRect = () => {
      if (!image.complete || !image.naturalWidth || !image.naturalHeight) return null;
      const rect = image.getBoundingClientRect();
      const scale = Math.min(rect.width / image.naturalWidth, rect.height / image.naturalHeight);
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      if (!width || !height) return null;
      return {
        left: rect.left + (rect.width - width) / 2,
        top: rect.top + (rect.height - height) / 2,
        width,
        height,
      };
    };

    const insidePhoto = (rect, x, y) => rect && x >= rect.left && x <= rect.left + rect.width
      && y >= rect.top && y <= rect.top + rect.height;

    const hideLoupe = () => {
      mode = null;
      loupe.classList.remove('is-visible');
      wheelDelta = 0;
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
      const pointer = touchPointer;
      touchPointer = null;
      if (pointer !== null && image.hasPointerCapture(pointer)) image.releasePointerCapture(pointer);
    };

    const updateLoupe = () => {
      frame = null;
      if (!mode || !viewer.open) return;
      const photo = photoRect();
      if (!photo || (mode === 'hover' && !insidePhoto(photo, point.x, point.y))) {
        hideLoupe();
        return;
      }
      const bounds = stage.getBoundingClientRect();
      const radius = loupe.offsetWidth / 2;
      const x = mode === 'keyboard' ? photo.left + point.x * photo.width
        : clamp(point.x, photo.left, photo.left + photo.width);
      const y = mode === 'keyboard' ? photo.top + point.y * photo.height
        : clamp(point.y, photo.top, photo.top + photo.height);
      let centreY = y;

      /* Keep the glass clear of the finger, but still magnify the point it touches. */
      if (mode === 'touch') {
        centreY = y - radius - 28;
        if (centreY - radius < bounds.top) centreY = y + radius + 28;
      }

      loupe.style.left = `${x - bounds.left - radius}px`;
      loupe.style.top = `${centreY - bounds.top - radius}px`;
      loupe.style.backgroundImage = `url(${JSON.stringify(image.currentSrc)})`;
      loupe.style.backgroundSize = `${photo.width * zoom}px ${photo.height * zoom}px`;
      loupe.style.backgroundPosition = `${radius - (x - photo.left) * zoom}px ${radius - (y - photo.top) * zoom}px`;
      loupe.classList.add('is-visible');
    };

    const scheduleUpdate = () => {
      if (mode && frame === null) frame = window.requestAnimationFrame(updateLoupe);
    };

    const setLevel = (value) => {
      zoom = clamp(minimum + Math.round((value - minimum) / step) * step, minimum, maximum);
      level.value = String(zoom);
      levelOut.textContent = `${zoom}×`;
      level.setAttribute('aria-valuetext', level.dataset.valueText.replace('__value__', String(zoom)));
      level.style.setProperty('--fill', `${(zoom - minimum) / (maximum - minimum) * 100}%`);
      scheduleUpdate();
    };

    setLevel(zoom);
    level.addEventListener('input', () => setLevel(Number(level.value)));
    level.addEventListener('change', () => setLevel(Number(level.value)));

    const show = (index) => {
      current = (index + links.length) % links.length;
      const link = links[current];
      hideLoupe();
      mouseBackdrop = false;
      image.src = link.dataset.fullSrc;
      image.alt = link.dataset.fullAlt || '';
      if (link.dataset.fullWidth) image.width = Number(link.dataset.fullWidth);
      if (link.dataset.fullHeight) image.height = Number(link.dataset.fullHeight);
      if (count) count.textContent = links.length > 1 ? `${current + 1} / ${links.length}` : '';
    };

    links.forEach((link, index) => {
      link.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
        event.preventDefault();
        if (viewer.open) return;
        opener = link;
        show(index);
        hint.textContent = window.matchMedia('(hover: hover) and (pointer: fine)').matches
          ? hoverHint : hint.dataset.hintTouch;
        previousOverflow = document.documentElement.style.overflow;
        viewer.showModal();
        document.documentElement.style.overflow = 'hidden';
        viewer.querySelector('[data-viewer-close]').focus();
      });
    });

    viewer.addEventListener('close', () => {
      document.documentElement.style.overflow = previousOverflow;
      hideLoupe();
      mouseBackdrop = false;
      if (opener) opener.focus();
    });

    viewer.querySelector('[data-viewer-close]').addEventListener('click', () => viewer.close());
    viewer.querySelector('[data-viewer-prev]')?.addEventListener('click', () => show(current - 1));
    viewer.querySelector('[data-viewer-next]')?.addEventListener('click', () => show(current + 1));
    viewer.querySelectorAll('.pdp-zoom__nav').forEach((button) => {
      button.addEventListener('pointerenter', hideLoupe);
    });

    const movePointer = (event) => {
      if (event.pointerType === 'touch') {
        if (event.pointerId !== touchPointer) return;
      } else if ((event.pointerType === 'mouse' || event.pointerType === 'pen') && touchPointer === null) {
        mode = 'hover';
      } else {
        return;
      }
      point = { x: event.clientX, y: event.clientY };
      scheduleUpdate();
    };

    image.addEventListener('pointerenter', movePointer);
    image.addEventListener('pointermove', movePointer);
    image.addEventListener('pointerleave', () => {
      if (mode === 'hover') hideLoupe();
    });
    image.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch' || touchPointer !== null
        || !insidePhoto(photoRect(), event.clientX, event.clientY)) return;
      event.preventDefault();
      hideLoupe();
      touchPointer = event.pointerId;
      mode = 'touch';
      image.setPointerCapture(touchPointer);
      movePointer(event);
    });
    const endTouch = (event) => {
      if (event.pointerId === touchPointer) hideLoupe();
    };
    image.addEventListener('pointerup', endTouch);
    image.addEventListener('pointercancel', endTouch);
    image.addEventListener('lostpointercapture', endTouch);
    image.addEventListener('dragstart', (event) => event.preventDefault());
    image.addEventListener('load', scheduleUpdate);
    window.addEventListener('resize', scheduleUpdate);

    /* Trackpads send many small deltas. Gather those, then take one slider step. */
    image.addEventListener('wheel', (event) => {
      const photo = photoRect();
      if (!loupe.classList.contains('is-visible') || !insidePhoto(photo, event.clientX, event.clientY)) return;
      event.preventDefault();
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? photo.height : 1);
      if (delta * wheelDelta < 0) wheelDelta = 0;
      wheelDelta += delta;
      if (Math.abs(wheelDelta) >= 40) {
        setLevel(zoom + (wheelDelta < 0 ? step : -step));
        wheelDelta = 0;
      }
    }, { passive: false });

    /* A backdrop close must start with a mouse, never the click after a touch drag. */
    stage.addEventListener('pointerdown', (event) => {
      mouseBackdrop = event.pointerType === 'mouse' && event.button === 0
        && !insidePhoto(photoRect(), event.clientX, event.clientY);
    });
    stage.addEventListener('click', (event) => {
      if (mouseBackdrop && !insidePhoto(photoRect(), event.clientX, event.clientY)) viewer.close();
      mouseBackdrop = false;
    });
    stage.addEventListener('blur', () => {
      if (mode === 'keyboard') hideLoupe();
    });

    viewer.addEventListener('keydown', (event) => {
      /* The range and buttons own their keys, including left and right arrows. */
      if (event.target.closest('input, button, select, textarea, a, [contenteditable]')) return;
      if (event.target === stage) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (mode === 'keyboard') {
            hideLoupe();
          } else {
            hideLoupe();
            mode = 'keyboard';
            point = { x: 0.5, y: 0.5 };
            scheduleUpdate();
          }
          return;
        }
        if (mode === 'keyboard') {
          const distance = event.shiftKey ? 0.15 : 0.05;
          switch (event.key) {
            case 'ArrowLeft': point.x = clamp(point.x - distance, 0, 1); break;
            case 'ArrowRight': point.x = clamp(point.x + distance, 0, 1); break;
            case 'ArrowUp': point.y = clamp(point.y - distance, 0, 1); break;
            case 'ArrowDown': point.y = clamp(point.y + distance, 0, 1); break;
            case '+':
            case '=': setLevel(zoom + step); break;
            case '-': setLevel(zoom - step); break;
            default: return;
          }
          event.preventDefault();
          scheduleUpdate();
          return;
        }
      }
      if (event.key === 'ArrowRight' && links.length > 1) { event.preventDefault(); show(current + 1); }
      if (event.key === 'ArrowLeft' && links.length > 1) { event.preventDefault(); show(current - 1); }
    });
  });
})();

(() => {
  const galleries = document.querySelectorAll('[data-product-gallery]');

  if (!galleries.length) return;

  galleries.forEach((gallery) => {
    const tablist = gallery.querySelector('[role="tablist"]');

    if (!tablist) return;

    const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
    const panels = Array.from(gallery.querySelectorAll('[role="tabpanel"]'));

    if (!tabs.length || !panels.length) return;

    /*
     * Below the desktop breakpoint the panels are a sideways strip (see the
     * gallery snippet's stylesheet), so a swipe changes the photograph and the
     * tabs scroll the strip rather than hiding panels. While the strip is in
     * use no panel carries [hidden] - Shopify's accelerated-checkout stylesheet
     * makes that attribute display: none !important, so it has to come off in
     * the markup rather than be overruled in CSS - and when the window grows to
     * the desktop layout the selected tab's panel is the one left showing.
     */
    const strip = gallery.querySelector('[data-gallery-strip]');
    const counter = gallery.querySelector('[data-gallery-counter]');
    const narrow = window.matchMedia('(max-width: 1023px)');
    const stripActive = () => Boolean(strip) && narrow.matches;

    const markTab = (selectedTab) => {
      tabs.forEach((tab) => {
        const isSelected = tab === selectedTab;
        tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        tab.setAttribute('tabindex', isSelected ? '0' : '-1');
      });
    };

    const syncStrip = () => {
      if (!strip) return;
      if (narrow.matches) {
        panels.forEach((panel) => {
          panel.hidden = false;
        });
        return;
      }
      const selected = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
      const selectedPanelId = selected.getAttribute('aria-controls');
      panels.forEach((panel) => {
        panel.hidden = panel.id !== selectedPanelId;
      });
    };

    syncStrip();
    narrow.addEventListener('change', syncStrip);

    const selectTab = (selectedTab, moveFocus = false) => {
      const selectedPanelId = selectedTab.getAttribute('aria-controls');

      markTab(selectedTab);

      if (stripActive()) {
        const target = panels.find((panel) => panel.id === selectedPanelId);
        if (target) target.scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'smooth' });
      } else {
        panels.forEach((panel) => {
          panel.hidden = panel.id !== selectedPanelId;
        });
      }

      if (moveFocus) selectedTab.focus();

      selectedTab.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'nearest',
      });
    };

    /* A swipe lands on a photograph: the counter and the thumbnail ring follow it. */
    if (strip && typeof IntersectionObserver === 'function') {
      const watcher = new IntersectionObserver(
        (entries) => {
          if (!stripActive()) return;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = panels.indexOf(entry.target);
            if (index < 0) return;
            if (counter) counter.textContent = `${index + 1} / ${panels.length}`;
            if (tabs[index]) markTab(tabs[index]);
          });
        },
        { root: strip, threshold: 0.6 }
      );
      panels.forEach((panel) => watcher.observe(panel));
    }

    /* The photograph is the biggest target on the page; a click on it opens the
       viewer the same way the disc in its corner does. */
    panels.forEach((panel) => {
      const image = panel.querySelector('.pdp-gallery__image');
      const zoom = panel.querySelector('[data-gallery-zoom]');
      if (!image || !zoom) return;
      image.addEventListener('click', () => zoom.click());
    });

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => selectTab(tab));

      tab.addEventListener('keydown', (event) => {
        let nextIndex;

        switch (event.key) {
          case 'ArrowRight':
            nextIndex = (index + 1) % tabs.length;
            break;
          case 'ArrowLeft':
            nextIndex = (index - 1 + tabs.length) % tabs.length;
            break;
          case 'Home':
            nextIndex = 0;
            break;
          case 'End':
            nextIndex = tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        selectTab(tabs[nextIndex], true);
      });
    });
  });
})();

/*
 * Brand plates: settle in as the wall reaches the viewport.
 *
 * A plate is hidden only at the moment it is handed to the observer, so nothing is
 * ever hidden without something watching to bring it back: a blocked script, a
 * failed file, or a plate added to the page after this ran all leave it visible.
 * The first version used a class on the root instead, and any plate that appeared
 * later was hidden by it and never observed - invisible for good.
 *
 * Reduced motion is honoured by the stylesheet, which never hides the plates in the
 * first place under that query - so this observer is skipped entirely there rather
 * than doing work whose result is not used.
 */
(() => {
  /* Marquee tiles move through a clipped scrollport, so fading them by viewport
     intersection leaves blank slots at the leading edge. They stay fully visible. */
  const plates = document.querySelectorAll('.brand-plate:not(.brand-strip__tile)');
  if (!plates.length) return;

  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (still || !('IntersectionObserver' in window)) {
    plates.forEach((plate) => plate.classList.add('is-in'));
    return;
  }

  /* Stagger by position within the plate's own grid, so the homepage band of twelve
     and the index of forty both read left to right rather than by document order
     across sections. Capped: past the eighth plate every one shares the last delay,
     which keeps a long index from running a queue the reader has to wait out. */
  const STEP = 45;
  const CAP = 8;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const plate = entry.target;
        const siblings = Array.from(plate.closest('ul')?.children || []);
        const index = siblings.indexOf(plate.parentElement);
        plate.style.setProperty('--plate-delay', `${Math.min(index, CAP) * STEP}ms`);
        plate.classList.remove('brand-plate--pending');
        plate.classList.add('is-in');
        observer.unobserve(plate);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  plates.forEach((plate) => {
    // Hide and observe in the same breath, so nothing is ever hidden without
    // something watching to bring it back.
    plate.classList.add('brand-plate--pending');
    observer.observe(plate);
  });
})();

/*
 * The brand strip.
 *
 * The strip advances by scrolling itself. It was a CSS transform animation over
 * a hidden overflow, which looks identical and behaves nothing like it: the box
 * did not scroll, the content slid beneath it, so there was nothing under the
 * cursor to catch and nothing to push back. Hovering did not stop it either,
 * because animation-play-state was never set.
 *
 * Driving scrollLeft instead means hover, focus, drag, wheel, trackpad and a
 * finger on glass are all the same mechanism, and the browser supplies most of
 * them for free.
 */
(() => {
  const strips = document.querySelectorAll('[data-brand-strip]');

  if (!strips.length) return;

  const stillness = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Below this a pointer press is a click on a logo, not a drag of the strip.
  const DRAG_SLOP = 4;

  strips.forEach((viewport) => {
    const track = viewport.querySelector('[data-brand-strip-track]');
    const lap = track && track.firstElementChild;

    if (!lap) return;

    let held = false;
    let dragging = false;
    let gesturing = false;
    let momentum = null;
    let travelled = 0;
    let originX = 0;
    let originScroll = 0;
    let frame = null;
    let previous = 0;
    /*
     * The strip's own idea of where it is, kept as a float. scrollLeft may be
     * rounded to whole pixels by the browser, and at 90 seconds for a strip this
     * wide a frame is under two pixels, so accumulating on the element itself
     * would lose most of the movement to rounding and judder.
     */
    let position = viewport.scrollLeft;

    /*
     * Measured once, not every frame. step() writes scrollLeft and then read
     * lap.scrollWidth on the next tick, and a read after a write forces the
     * browser to lay the strip out again: 360 style recalcs in six idle seconds,
     * 28% of a throttled phone's main thread, for a strip that had scrolled off
     * the top of the page. The width only changes when the viewport does.
     */
    let width = lap.scrollWidth;
    const lapWidth = () => width;
    const remeasure = () => {
      width = lap.scrollWidth;
    };
    window.addEventListener('resize', remeasure, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(remeasure).observe(lap);

    const seconds = () => {
      const declared = parseFloat(getComputedStyle(viewport).getPropertyValue('--marquee-seconds'));

      return Number.isFinite(declared) && declared > 0 ? declared : 90;
    };

    // Wrap at exactly one list-width. The list is rendered twice, so the tile
    // arriving at the seam is the same tile already standing there.
    const wrap = (value, width) => {
      if (width <= 0) return value;
      if (value >= width) return value - width;
      if (value < 0) return value + width;

      return value;
    };

    const step = (now) => {
      const elapsed = previous ? now - previous : 0;
      previous = now;

      const width = lapWidth();

      if (!held && !dragging && !gesturing && width > 0 && elapsed > 0 && elapsed < 250) {
        // Anything that moved the scroll other than us - a drag, a wheel, a link
        // taking focus - wins, and the strip carries on from there. Our own step
        // is a pixel or two, so the threshold cannot mistake one for the other.
        if (Math.abs(viewport.scrollLeft - position) > 2) position = viewport.scrollLeft;

        position = wrap(position + (width / (seconds() * 1000)) * elapsed, width);
        viewport.scrollLeft = position;
      }

      frame = window.requestAnimationFrame(step);
    };

    const start = () => {
      if (frame !== null || stillness.matches) return;
      previous = 0;
      position = viewport.scrollLeft;
      frame = window.requestAnimationFrame(step);
    };

    const stop = () => {
      if (frame === null) return;
      window.cancelAnimationFrame(frame);
      frame = null;
    };

    const hold = () => {
      held = true;
    };

    const release = () => {
      held = false;
      position = viewport.scrollLeft;
    };

    /*
     * Any pointer at all holds the strip, not just a mouse that entered the box.
     * On glass there is no mouseenter, so a finger swipe would have been fought
     * for its whole length by the driver still writing scrollLeft underneath it.
     *
     * The release waits, because a flick on a touch screen keeps scrolling after
     * the finger has gone and resuming into that momentum snatches it away.
     */
    const gestureStart = () => {
      window.clearTimeout(momentum);
      gesturing = true;
    };

    const gestureEnd = () => {
      window.clearTimeout(momentum);
      momentum = window.setTimeout(() => {
        gesturing = false;
        position = viewport.scrollLeft;
      }, 1200);
    };

    viewport.addEventListener('pointerdown', gestureStart);
    viewport.addEventListener('pointerup', gestureEnd);
    viewport.addEventListener('pointercancel', gestureEnd);

    viewport.addEventListener('mouseenter', hold);
    viewport.addEventListener('mouseleave', release);
    // A brand reached by keyboard holds the strip too, or it walks out from
    // under the focus ring while you are reading it.
    viewport.addEventListener('focusin', hold);
    viewport.addEventListener('focusout', (event) => {
      if (!viewport.contains(event.relatedTarget)) release();
    });

    viewport.addEventListener('pointerdown', (event) => {
      // Touch and pen already pan this box natively, and hijacking them costs
      // the momentum and the rubber band. Mouse has no such gesture, so it is
      // the only one that needs teaching.
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      dragging = true;
      travelled = 0;
      originX = event.clientX;
      originScroll = viewport.scrollLeft;
      viewport.classList.add('is-dragging');
      viewport.setPointerCapture(event.pointerId);
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!dragging) return;

      const shift = event.clientX - originX;
      const width = lapWidth();

      travelled = Math.max(travelled, Math.abs(shift));

      const next = wrap(originScroll - shift, width);

      // Re-origin on the wrap, so a drag that crosses the seam keeps tracking
      // the hand instead of snapping a full list-width away from it.
      if (width > 0 && Math.abs(next - (originScroll - shift)) > 1) {
        originScroll = next + shift;
      }

      viewport.scrollLeft = next;
      position = next;
      event.preventDefault();
    });

    const drop = (event) => {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-dragging');

      if (viewport.hasPointerCapture(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }
    };

    viewport.addEventListener('pointerup', drop);
    viewport.addEventListener('pointercancel', drop);

    // A drag that ends on a logo must not also open it. Captured, so it lands
    // before the anchor's own default.
    viewport.addEventListener(
      'click',
      (event) => {
        if (travelled <= DRAG_SLOP) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    viewport.addEventListener('dragstart', (event) => event.preventDefault());

    /*
     * Runs only while it can be seen. The loop used to re-arm itself for as long
     * as the page was open, tab hidden or strip scrolled away; now the strip
     * parks when it leaves the viewport or the tab goes to the background and
     * picks up where it stood when it returns.
     */
    let visible = true;
    const settle = () => {
      if (stillness.matches || !visible || document.hidden) stop();
      else start();
    };

    if (typeof stillness.addEventListener === 'function') {
      stillness.addEventListener('change', settle);
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          visible = entries.some((entry) => entry.isIntersecting);
          settle();
        },
        { rootMargin: '100px 0px' }
      ).observe(viewport);
    }
    document.addEventListener('visibilitychange', settle);

    settle();
  });
})();

/*
 * The second product view.
 *
 * The swap is gated on the image having actually arrived. It is lazy, so on a
 * long collection page the first hover on a card below the fold used to reveal
 * a frame that had not loaded - which read as the swap being broken, because
 * from the outside it is.
 */
(() => {
  const alternates = document.querySelectorAll('.product-card__image--alt');

  if (!alternates.length) return;

  alternates.forEach((image) => {
    if (image.complete && image.naturalWidth > 0) {
      image.classList.add('is-ready');

      return;
    }

    image.addEventListener('load', () => image.classList.add('is-ready'), { once: true });
  });
})();

/*
 * Section reveal.
 *
 * Sections rise a few pixels into place the first time they are reached. It is the
 * smallest amount of motion that still tells you the page has depth, and it is
 * deliberately not a "fade in from far away" - the content is already where it
 * belongs, it just arrives.
 *
 * Three rules it obeys:
 *
 *   Nothing is hidden unless something is watching to bring it back. The pending
 *   class is added in the same statement that starts observing, so a thrown
 *   exception between the two cannot leave a section invisible. Without JavaScript
 *   the class is never added at all and every section is simply visible.
 *
 *   The first section never animates. It is on screen before the observer runs and
 *   it holds the hero image, so delaying its paint would cost the largest
 *   contentful paint for the sake of a movement nobody sees.
 *
 *   Under prefers-reduced-motion this does not run. Not "runs faster" - the whole
 *   observer is skipped, because a reduced-motion user asked for no motion, not
 *   for brisk motion.
 */
(() => {
  const stillness = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (stillness.matches) return;
  if (typeof IntersectionObserver === 'undefined') return;

  const sections = Array.from(document.querySelectorAll('main section.full-width'));

  // The first is already in view. Everything after it is worth the eight pixels.
  sections.shift();

  if (!sections.length) return;

  /*
   * Two more things never animate, and for the same reason: the rise is for
   * bands the reader scrolls TO, not for things already painted.
   *
   *   Listing grids. The catalogue and search grids are what those pages are for,
   *   and this script runs deferred - so the grid had painted, was taken to
   *   opacity 0, and faded back after it was 12% inside the window. Every visitor
   *   saw the shelf blink.
   *
   *   Anything already on screen, or within a tenth of a screen of it. A band
   *   whose top is inside the first viewport is being read as the script arrives;
   *   hiding it to reveal it is a beat of nothing for no movement anyone sees.
   */
  const LISTING = '.main-collection, .main-search';
  const vh = window.innerHeight;
  const alreadyOnScreen = (element) => element.getBoundingClientRect().top < vh * 1.1;

  const observer = new IntersectionObserver(
    (entries, self) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('reveal--pending');
        entry.target.classList.add('reveal--in');
        self.unobserve(entry.target);
      });
    },
    /*
     * A band settles as its top reaches the viewport, not after it is 12% inside:
     * the margin extends the root downwards, so the movement has finished by the
     * time the reader's eye gets there.
     *
     * threshold 0, not 0.04. A threshold is a fraction of the TARGET's area, and these
     * targets are whole sections: on a collection page the product section is over
     * 4000px tall, so 4% of it is 160px, and in a short window - a phone in landscape,
     * a small desktop window - that much of it could not be on screen at once. The
     * section then sat at opacity 0 until the first scroll. Found at a 460px viewport,
     * where the collection's house rail was simply missing from the first paint.
     */
    { rootMargin: '0px 0px 10% 0px', threshold: 0 }
  );

  sections.forEach((section) => {
    if (section.matches(LISTING)) return;
    if (alreadyOnScreen(section)) return;

    section.classList.add('reveal--pending');
    observer.observe(section);
  });

  /*
   * The second tier: inside a band, the things ON the shelf arrive one after another
   * rather than as one slab. A shelf of eight frames appearing simultaneously reads
   * as a page loading; the same eight arriving left to right reads as a shop being
   * laid out, and it is the difference between a grid and a display.
   *
   * A group opts in with data-reveal-group on the list. Only the first STAGGER_MAX
   * children are staggered - past about half a second the delay stops reading as
   * choreography and starts reading as latency - and the rest come with the group.
   *
   * The delay is inline because it is per-child arithmetic, not a design token, and
   * it is cleared the moment the transition ends so a later hover or filter repaint
   * is never held behind a stale 420ms.
   */
  const STAGGER_STEP = 40;
  const STAGGER_MAX = 6;

  const groupObserver = new IntersectionObserver(
    (entries, self) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        Array.from(entry.target.children).forEach((child, index) => {
          if (index < STAGGER_MAX) {
            child.style.transitionDelay = `${index * STAGGER_STEP}ms`;
            child.addEventListener(
              'transitionend',
              () => {
                child.style.transitionDelay = '';
              },
              { once: true }
            );
          }

          child.classList.remove('reveal--pending');
          child.classList.add('reveal--in');
        });

        self.unobserve(entry.target);
      });
    },
    // Same reasoning as above: a thirty-card grid is taller than most windows.
    { rootMargin: '0px 0px 10% 0px', threshold: 0 }
  );

  document.querySelectorAll('[data-reveal-group]').forEach((group) => {
    if (group.closest(LISTING)) return;
    if (alreadyOnScreen(group)) return;

    Array.from(group.children).forEach((child) => child.classList.add('reveal--pending'));
    groupObserver.observe(group);
  });

  /*
   * Fail open. Anything that renders the page without scrolling it - print, a
   * PDF of a frame page, a preview thumbnail - and anything that moves the
   * viewport without a scroll event the observers see first - a bfcache restore,
   * the /#fit-finder door - gets every band at once. And a page short enough to
   * fit its window has nothing to reveal, so it shows everything on load.
   *
   * Not a blanket timeout: that would strip the rise from every band for anyone
   * who reads the hero for longer than the timer.
   */
  const revealAll = () => {
    document.querySelectorAll('.reveal--pending').forEach((element) => {
      element.classList.remove('reveal--pending');
      element.classList.add('reveal--in');
    });
  };

  window.addEventListener('beforeprint', revealAll);
  window.addEventListener('pagehide', revealAll);
  window.addEventListener('hashchange', revealAll);
  window.addEventListener(
    'load',
    () => {
      const root = document.documentElement;

      if (root.clientHeight >= root.scrollHeight - 1) revealAll();
    },
    { once: true }
  );
})();

/*
 * Frame anatomy: turning the drawing.
 *
 * The band draws a pair of spectacles in CSS 3D and turns it as the plate crosses
 * the viewport. The turn is the explanation, not the ornament: head-on, the arms
 * point straight away from the reader and vanish, which is precisely why a
 * photograph of a frame can never tell you its temple length. Rotating it produces
 * the third measurement out of nowhere, and the list beside the drawing lights the
 * row the drawing is currently showing.
 *
 * Everything below is an enhancement over a drawing that is already complete. The
 * stylesheet holds a resting three-quarter view, so with this script absent, with
 * JavaScript off, or under prefers-reduced-motion, the reader still gets the whole
 * object - simply still. Nothing is hidden waiting to be revealed.
 *
 * Two things decide the angle, and neither is allowed to have an edge.
 *
 *   Scroll sets the base angle. It is eased at both ends rather than mapped
 *   straight, so the drawing gathers and settles instead of starting and stopping
 *   at a line.
 *
 *   The pointer leans it. The first version listened on the plate itself, which
 *   put a hard boundary around the whole effect: crossing the plate's edge jumped
 *   the target by the full lean in one frame, and leaving snapped it back to
 *   nothing. The pointer is now followed across the document and its influence is
 *   scaled by how near it is, reaching zero a plate-width away - so the lean grows
 *   and fades with the approach and there is no line anywhere to cross. It also
 *   leans the right way now: the first version raised the camera when the pointer
 *   went down.
 *
 * The loop only runs while the plate is on screen, and a reader who switches
 * reduced motion on mid-visit has it stopped and the inline properties handed back
 * to the stylesheet rather than frozen at whatever angle they were at.
 */
(() => {
  const bands = document.querySelectorAll('[data-frame-anatomy]');

  if (!bands.length) return;

  const stillness = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (typeof IntersectionObserver === 'undefined') return;

  // Degrees of yaw across one viewport of scrolling. The range deliberately never
  // reaches zero: dead-on, the arms have no length to show and the drawing loses a
  // third of itself. The floor and ceiling hold that true once a lean is added.
  const MIN_TURN = 12;
  const MAX_TURN = 66;
  const TURN_FLOOR = 8;
  const TURN_CEIL = 74;
  const POINTER_TURN = 10;
  const POINTER_TILT = 4;
  const BASE_TILT = -7;
  // How far from the plate the pointer still counts, in plate half-widths. 1 is the
  // plate's own edge, so the fall-off happens entirely outside it and the reader
  // never feels the frame of the drawing as a switch.
  const POINTER_REACH = 2.2;
  // Approach per 60Hz frame. Applied through the elapsed time below, so a 120Hz
  // display gets the same glide rather than twice the speed.
  const EASE = 0.09;
  // A dead band around the hand-over, so a plate parked near the threshold cannot
  // flicker the list between two rows.
  const TO_SIDE = 44;
  const TO_FRONT = 40;
  /*
   * Where the drawing has to stand to answer "which one is that". Eye and bridge
   * want the front; the temple wants the profile, because from the front the arm
   * points straight away from the reader and there is nothing to point at. Turning
   * to show the named part is the answer to the question, not decoration on it.
   */
  const PART_TURN = { eye: 20, bridge: 11, temple: 64 };
  // The pointer still leans it while a part is named, but only lightly - at full
  // weight it can drag the temple back towards the angle that hides it.
  const PART_LEAN = 0.35;

  const clamp = (value, low, high) => Math.min(Math.max(value, low), high);

  const smooth = (edge, stop, value) => {
    const t = clamp((value - edge) / (stop - edge), 0, 1);

    return t * t * (3 - 2 * t);
  };

  // One pointer, shared by every band on the page. Following it on the document
  // rather than on the plate is the whole point: the reading stays continuous as it
  // crosses the plate's edge, so nothing jumps and nothing snaps back.
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const pointer = { x: 0, y: 0, live: false };

  document.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType === 'touch' || !fine.matches) return;
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      pointer.live = true;
    },
    { passive: true }
  );

  const lostPointer = () => {
    pointer.live = false;
  };

  document.documentElement.addEventListener('pointerleave', lostPointer);
  window.addEventListener('blur', lostPointer);

  bands.forEach((band) => {
    const stage = band.querySelector('[data-frame-anatomy-stage]');

    if (!stage) return;

    const toggles = Array.from(band.querySelectorAll('.fanatomy__spec[data-part]'));

    let running = null;
    let previous = 0;
    let turn = (MIN_TURN + MAX_TURN) / 2;
    let tilt = BASE_TILT;
    let face = '';
    // Three ways to name a part, in order of how deliberate they are.
    let pinned = '';
    let hovered = '';
    let focused = '';

    const named = () => pinned || hovered || focused;

    const partOf = (node, within) => {
      const el = node && node.closest ? node.closest(within) : null;

      return el ? el.getAttribute('data-part') : '';
    };

    const applyPart = () => {
      const part = named();

      if (part) {
        band.setAttribute('data-part', part);
        // The automatic dimming that follows the turn stands down rather than
        // arguing with a part the reader has actually asked about. Clearing the
        // remembered face makes the loop restate it the moment they let go.
        band.removeAttribute('data-face');
        face = '';
      } else {
        band.removeAttribute('data-part');
      }

      toggles.forEach((toggle) => {
        const mine = toggle.getAttribute('data-part');

        toggle.classList.toggle('is-lit', Boolean(part) && mine === part);
        toggle.setAttribute('aria-pressed', mine === pinned ? 'true' : 'false');
      });
    };

    // Where the scroll alone would hold the drawing, with no lean applied.
    const carried = (rect, height) => {
      const travelled = clamp(1 - (rect.top + rect.height / 2) / height, 0, 1);

      return MIN_TURN + (MAX_TURN - MIN_TURN) * smooth(0, 1, travelled);
    };

    // -1 to 1 on each axis, faded out by distance, zero when the pointer is far
    // away or is a finger.
    const lean = (rect) => {
      const halfWidth = rect.width / 2;
      const halfHeight = rect.height / 2;

      if (!pointer.live || !fine.matches || halfWidth <= 0 || halfHeight <= 0) {
        return { x: 0, y: 0 };
      }

      const dx = (pointer.x - (rect.left + halfWidth)) / halfWidth;
      const dy = (pointer.y - (rect.top + halfHeight)) / halfHeight;
      const nearness = 1 - smooth(1, POINTER_REACH, Math.hypot(dx, dy));

      return { x: clamp(dx, -1, 1) * nearness, y: clamp(dy, -1, 1) * nearness };
    };

    const angles = () => {
      const rect = stage.getBoundingClientRect();
      const height = window.innerHeight || document.documentElement.clientHeight || 1;
      const pull = lean(rect);

      const part = named();
      const base = Object.prototype.hasOwnProperty.call(PART_TURN, part)
        ? PART_TURN[part]
        : carried(rect, height);
      const weight = part ? PART_LEAN : 1;

      return {
        turn: clamp(base + pull.x * POINTER_TURN * weight, TURN_FLOOR, TURN_CEIL),
        // Pointer above the plate raises the camera, which is the direction a person
        // expects the object to tip when they reach over it.
        tilt: BASE_TILT + pull.y * POINTER_TILT * weight,
      };
    };

    const paint = () => {
      stage.style.setProperty('--turn', `${turn.toFixed(2)}deg`);
      stage.style.setProperty('--tilt', `${tilt.toFixed(2)}deg`);
      // How much of the front still faces the reader. The lens sheen reads off it.
      stage.style.setProperty('--face', Math.abs(Math.cos((turn * Math.PI) / 180)).toFixed(3));

      if (named()) return;

      // Seeded from the midpoint on the first frame after a part is let go, so
      // landing inside the dead band resolves rather than keeping a stale answer.
      let showing = face || (turn >= (TO_SIDE + TO_FRONT) / 2 ? 'side' : 'front');
      if (turn >= TO_SIDE) showing = 'side';
      if (turn <= TO_FRONT) showing = 'front';

      if (showing && showing !== face) {
        face = showing;
        band.setAttribute('data-face', showing);
      }
    };

    const step = (now) => {
      // A tab left in the background hands back one enormous gap on return. Capping
      // it keeps that from resolving to a single jump.
      const elapsed = previous ? Math.min(now - previous, 100) : 1000 / 60;
      previous = now;

      const wanted = angles();
      const approach = 1 - Math.pow(1 - EASE, elapsed / (1000 / 60));

      turn += (wanted.turn - turn) * approach;
      tilt += (wanted.tilt - tilt) * approach;

      paint();

      running = window.requestAnimationFrame(step);
    };

    const start = () => {
      if (running !== null || stillness.matches) return;

      // Arrive at the angle the scroll asks for rather than easing in from wherever
      // it was left. The observer fires before the plate is in view, so this is
      // never seen, and it stops a fast scroll past and back from swinging it.
      const settled = angles();
      turn = settled.turn;
      tilt = settled.tilt;
      previous = 0;
      paint();

      stage.classList.add('is-turning');
      running = window.requestAnimationFrame(step);
    };

    const stop = () => {
      stage.classList.remove('is-turning');
      if (running === null) return;
      window.cancelAnimationFrame(running);
      running = null;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            start();

            return;
          }

          stop();
        });
      },
      // A little before the edge, so the plate is already turning when it arrives.
      { rootMargin: '15% 0px' }
    );

    observer.observe(stage);

    /*
     * Naming a part, from either end.
     *
     * One set of delegated listeners covers both the three rows and the pieces of
     * the drawing, because both carry the same data-part. A finger is ignored here:
     * pointerover fires once on a tap and would leave a stale highlight nothing
     * clears, so touch goes through the press below instead, which is also the only
     * way a phone could reach this at all.
     */
    band.addEventListener('pointerover', (event) => {
      if (event.pointerType === 'touch') return;

      const part = partOf(event.target, '[data-part]');

      if (part === hovered) return;

      hovered = part;
      applyPart();
    });

    band.addEventListener('pointerleave', () => {
      if (!hovered) return;

      hovered = '';
      applyPart();
    });

    /*
     * Keyboard focus names a part; a mouse click's focus does not. Clicking a row
     * focuses it like any button, and treating that as a name left the row lit long
     * after the reader had moved on, with no obvious way to put it out. :focus-visible
     * is the browser's own answer to "did they mean to be here", and it is the same
     * test the theme's focus ring already uses.
     */
    band.addEventListener('focusin', (event) => {
      const target = event.target;
      let deliberate = false;

      try {
        deliberate = Boolean(target && target.matches && target.matches(':focus-visible'));
      } catch (error) {
        deliberate = false;
      }

      focused = deliberate ? partOf(target, '.fanatomy__spec[data-part]') : '';
      applyPart();
    });

    band.addEventListener('focusout', () => {
      if (!focused) return;

      focused = '';
      applyPart();
    });

    // Pressing a row holds it, which is what makes this reachable by tap and by
    // Tab rather than by mouse alone. Pressing it again, or Escape, lets it go.
    band.addEventListener('click', (event) => {
      const part = partOf(event.target, '.fanatomy__spec[data-part]');

      if (!part) return;

      pinned = pinned === part ? '' : part;
      applyPart();
    });

    band.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !pinned) return;

      pinned = '';
      applyPart();
    });

    const settle = () => {
      if (!stillness.matches) return;

      stop();
      band.removeAttribute('data-face');
      // Hand the angles back to the stylesheet instead of freezing them here.
      ['--turn', '--tilt', '--face'].forEach((name) => stage.style.removeProperty(name));
    };

    if (typeof stillness.addEventListener === 'function') {
      stillness.addEventListener('change', settle);
    }

    settle();
  });
})();

/*
 * The shelf, under a pointer.
 *
 * Each product photograph tips a few degrees toward the cursor. The stylesheet owns
 * the transform; this only writes two angles onto the media box, so with no
 * JavaScript - or under prefers-reduced-motion, where the rule that reads them does
 * not exist - the cards are simply flat.
 *
 * One listener per grid, not one per card, and the work is deferred to a frame:
 * getBoundingClientRect is a layout read, and doing it inline on every pointermove
 * across a thirty-card shelf is a forced synchronous layout per event. The rect is
 * read once per frame for the one card under the cursor.
 */
(() => {
  const grids = document.querySelectorAll('.product-grid, .featured-collection__grid, .product-related__grid');

  if (!grids.length) return;

  const precise = window.matchMedia('(hover: hover) and (pointer: fine)');
  const motionOK = window.matchMedia('(prefers-reduced-motion: no-preference)');

  // Degrees. Past about six the card stops reading as an object being looked at.
  const MAX_TURN = 4.5;
  const MAX_PITCH = 3.2;

  const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

  grids.forEach((grid) => {
    let pending = 0;
    let point = null;
    let lit = null;

    const flatten = (media) => {
      if (!media) return;

      media.style.removeProperty('--card-turn');
      media.style.removeProperty('--card-pitch');
    };

    const apply = () => {
      pending = 0;

      if (!point) return;

      const { media, x, y } = point;
      const rect = media.getBoundingClientRect();

      if (rect.width <= 0 || rect.height <= 0) return;

      const dx = clamp((x - (rect.left + rect.width / 2)) / (rect.width / 2), -1, 1);
      const dy = clamp((y - (rect.top + rect.height / 2)) / (rect.height / 2), -1, 1);

      // Positive rotateY turns the right edge away, so the near edge follows the
      // cursor rather than running from it.
      media.style.setProperty('--card-turn', `${(dx * MAX_TURN).toFixed(2)}deg`);
      media.style.setProperty('--card-pitch', `${(-dy * MAX_PITCH).toFixed(2)}deg`);
    };

    grid.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType !== 'mouse' || !precise.matches || !motionOK.matches) return;

        const card = event.target.closest && event.target.closest('.product-card');
        const media = card && card.querySelector('.product-card__media');

        if (!media) return;

        if (lit && lit !== media) flatten(lit);

        lit = media;
        point = { media, x: event.clientX, y: event.clientY };

        if (!pending) pending = window.requestAnimationFrame(apply);
      },
      { passive: true }
    );

    const release = () => {
      if (pending) {
        window.cancelAnimationFrame(pending);
        pending = 0;
      }

      point = null;
      flatten(lit);
      lit = null;
    };

    grid.addEventListener('pointerleave', release);
    // A card can also be left by the page moving under a still cursor.
    window.addEventListener('blur', release);
  });
})();

/*
 * The product page's buy bar.
 *
 * It follows the reader down once the real Add to Bag has left the top of the
 * screen, and gets out of the way again at the foot of the page, where the bag is no
 * longer what somebody is looking for and a fixed strip is only covering the
 * addresses.
 *
 * "Not visible" is not the test, and the first version used it. On a phone the real
 * button sits below the photograph, so it is off screen at the top of the page too -
 * and the bar came up before the visitor had seen the frame, let alone the button it
 * is a copy of. What matters is whether the button has been scrolled PAST.
 *
 * That cannot be done with IntersectionObserver alone, which is what the second
 * version tried. An observer only reports a change of intersection state, and going
 * from below the viewport to above it is not one: both are "not intersecting". A
 * single scrollTo from the top of the page to the middle produced no callback at
 * all, and the bar never appeared - measured, with the button's own rect sitting at
 * -539 while the bar stayed down.
 *
 * So the position is read on scroll, throttled to one frame. It is one
 * getBoundingClientRect on one element per frame while the page is actually moving,
 * which is the cheapest correct thing available; the observer stays for the footer,
 * where a state change is exactly what is being asked about.
 *
 * The bar carries no hiding attribute and no display: none of its own - the class
 * that lifts it is only ever added here - so a visitor whose script never runs is
 * never left with a strip permanently covering the bottom of the page.
 */
(() => {
  const bar = document.querySelector('[data-pdp-buybar]');

  if (!bar) return;

  const purchase = document.querySelector('.pdp-info__purchase');

  if (!purchase) return;

  const tail = document.querySelector('.shopify-section-group-footer-group');
  let tailVisible = false;
  let frame = 0;

  const settle = () => {
    frame = 0;

    const scrolledPast = purchase.getBoundingClientRect().bottom <= 0;

    bar.classList.toggle('is-up', scrolledPast && !tailVisible);
  };

  const schedule = () => {
    if (frame) return;

    frame = window.requestAnimationFrame(settle);
  };

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });

  if (tail && typeof IntersectionObserver === 'function') {
    new IntersectionObserver(
      (entries) => {
        tailVisible = entries.some((entry) => entry.isIntersecting);
        settle();
      },
      { rootMargin: '0px 0px -40px 0px' }
    ).observe(tail);
  }

  settle();
})();

/*
 * Predictive search.
 *
 * The panel is fetched as a rendered section - /search/suggest?section_id=
 * predictive-search - rather than from search/suggest.json, so the rows are built in
 * Liquid out of the same product data, the same measurement fields and the same money
 * filter as every other list on the site. A JSON endpoint would mean rebuilding all
 * of that here, in a second dialect, that could then drift from the first.
 *
 * The markup is a combobox in the ARIA sense: the input carries aria-expanded and
 * aria-activedescendant, and the panel is one listbox containing groups. Everything
 * ships inert - aria-expanded false, panel empty and hidden - and the form is an
 * ordinary GET, so with no JavaScript the field behaves exactly as it always did.
 *
 * Three things this has to get right and usually does not:
 *
 *   Out-of-order responses. A slow reply to "mas" must not overwrite a fast reply to
 *   "maske". Each request aborts the one before it, and the query it was made for is
 *   checked against the field before anything is written.
 *
 *   The pointer and the keyboard fighting. Moving the mouse clears the keyboard
 *   selection, so there is never a row highlighted by the arrow keys and a different
 *   row under the cursor.
 *
 *   Enter. With a row selected it follows that row; with nothing selected it submits
 *   the form and goes to the full results page, which is what a search field is for.
 */
(() => {
  const forms = document.querySelectorAll('[data-psearch]');

  if (!forms.length || typeof AbortController === 'undefined') return;

  const MIN_QUERY = 2;
  const DEBOUNCE = 180;
  const LIMIT = 6;

  forms.forEach((form) => {
    const input = form.querySelector('[data-psearch-input]');
    const panel = form.querySelector('[data-psearch-results]');
    const status = form.querySelector('[data-psearch-status]');

    if (!input || !panel) return;

    let timer = 0;
    let controller = null;
    let options = [];
    let active = -1;

    const say = (message) => {
      if (status) status.textContent = message;
    };

    const paint = () => {
      options.forEach((option, index) => {
        option.setAttribute('aria-selected', index === active ? 'true' : 'false');
      });

      if (active >= 0 && options[active]) {
        input.setAttribute('aria-activedescendant', options[active].id);
        options[active].scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    };

    const close = (clear) => {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1;
      options = [];

      if (clear) panel.replaceChildren();
    };

    const open = (html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const found = doc.querySelector('[data-psearch-panel]');

      if (!found) {
        close(true);
        say('');

        return;
      }

      panel.replaceChildren(...found.childNodes);
      options = Array.from(panel.querySelectorAll('[role="option"]'));

      if (!options.length) {
        close(true);

        return;
      }

      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      active = -1;
      paint();
      say(`${options.length} ${options.length === 1 ? 'result' : 'results'}`);
    };

    const search = (query) => {
      if (controller) controller.abort();

      controller = new AbortController();

      const url = `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}search/suggest`
        + `?q=${encodeURIComponent(query)}`
        + `&section_id=predictive-search`
        + `&resources[type]=product,collection`
        + `&resources[limit]=${LIMIT}`;

      fetch(url.replace('//search', '/search'), { signal: controller.signal })
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((html) => {
          // The field may have moved on while this was in flight.
          if (input.value.trim() !== query) return;

          open(html);
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;

          close(true);
        });
    };

    input.addEventListener('input', () => {
      const query = input.value.trim();

      window.clearTimeout(timer);

      if (query.length < MIN_QUERY) {
        if (controller) controller.abort();

        close(true);
        say('');

        return;
      }

      timer = window.setTimeout(() => search(query), DEBOUNCE);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close(true);

        return;
      }

      if (!options.length || panel.hidden) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();

        const step = event.key === 'ArrowDown' ? 1 : -1;
        /* The cycle runs over n + 1 places, because "nothing selected" is one of
           them: arrowing past the last row returns to the field, which is how a
           combobox lets you get back to what you typed. Shifted by one so the
           modulo has no negatives to deal with. */
        const places = options.length + 1;

        active = ((active + 1 + step + places) % places) - 1;

        paint();

        return;
      }

      if (event.key === 'Enter' && active >= 0 && options[active]) {
        event.preventDefault();
        options[active].click();
      }
    });

    // The pointer wins while it is moving, so the two never disagree.
    panel.addEventListener(
      'pointermove',
      () => {
        if (active === -1) return;

        active = -1;
        paint();
      },
      { passive: true }
    );

    form.addEventListener('focusout', () => {
      // Let focus land before deciding: focusout fires before the new element has it.
      window.setTimeout(() => {
        if (!form.contains(document.activeElement)) close(false);
      }, 0);
    });

    input.addEventListener('focus', () => {
      if (options.length) {
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
    });

    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) close(false);
    });
  });
})();

/*
 * Shape tiles: bring a keyboard-focused tile fully into the row.
 *
 * On a phone the shapes are a sideways row that snaps tile by tile. Chrome treats a
 * tile that is partly on screen as already visible when it takes focus, so Tab
 * stopped on Rectangular and Cat-eye with most of each tile past the edge. A mouse
 * or finger chooses what it can see, so only focus-visible focus moves the row.
 */
(() => {
  document.querySelectorAll('.shop-by-shape__list').forEach((list) => {
    list.addEventListener('focusin', (event) => {
      const item = event.target.closest('.shop-by-shape__item');
      if (!item) return;

      let deliberate = false;
      try {
        deliberate = event.target.matches(':focus-visible');
      } catch (error) {
        deliberate = false;
      }
      if (!deliberate) return;

      item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  });
})();

/*
 * Guide stock table on a phone.
 *
 * The seven-column table scrolls inside its own box and the phone draws no
 * scrollbar, so the right edge fades while there is more to the right and the
 * fade is dropped at the end. Under it, the first eight rows show and a button
 * reveals the rest; every row is in the DOM either way, so the table is whole
 * for a reader with no script and for a search engine.
 */
(() => {
  document.querySelectorAll('[data-guide-table-scroll]').forEach((box) => {
    const settle = () => {
      box.classList.toggle('is-at-end', box.scrollLeft + box.clientWidth >= box.scrollWidth - 2);
    };
    box.addEventListener('scroll', settle, { passive: true });
    window.addEventListener('resize', settle, { passive: true });
    /* The fonts arrive after the script and can widen the table. */
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(settle).observe(box);
    }
    settle();
  });

  document.querySelectorAll('[data-guide-frames-toggle]').forEach((button) => {
    const frames = button.closest('.guide__frames');
    if (!frames) return;
    button.addEventListener('click', () => {
      const open = frames.classList.toggle('guide__frames--open');
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      button.textContent = open ? button.dataset.labelFewer : button.dataset.labelMore;
    });
  });
})();

/*
 * Guide contents: mark the chapter in view.
 *
 * Only on a wide screen, where the contents list is sticky beside the text. A
 * chapter counts as current while it crosses a band 40% to 45% down the window;
 * when no chapter crosses it - the ~1,800px stock table between chapters 02 and
 * 03, say - the marker simply holds where it was rather than going blank. The
 * one exception is the top of the page: with the title, the lede and the
 * photograph on screen and chapter 01 still below the band, no chapter is
 * current, and a held marker there would point at a chapter the reader has
 * scrolled away from.
 */
(() => {
  const contents = document.querySelector('.guide__contents');
  if (!contents || typeof IntersectionObserver !== 'function') return;
  if (!window.matchMedia('(min-width: 1024px)').matches) return;

  const links = Array.from(contents.querySelectorAll('.guide__contents-link'));
  const targets = Array.from(document.querySelectorAll('.guide__chapter[id], .guide__questions[id]'));
  if (!links.length || !targets.length) return;

  const mark = (id) => {
    links.forEach((link) => {
      if (link.getAttribute('href') === `#${id}`) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting);
      if (!visible.length) {
        const aboveFirst = targets[0].getBoundingClientRect().top > window.innerHeight * 0.45;
        if (aboveFirst) mark('');
        return;
      }
      mark(visible[0].target.id);
    },
    { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
  );
  targets.forEach((target) => observer.observe(target));
})();
